import React, { useState, useEffect, useCallback, useRef } from 'react';
import './Settings.css';
import { useUser } from '../../../../UserContext';
import avatar from '../../../../assets/images/profile.png';
import { User, Camera, Bell, Shield, Eye, Mail, Phone, Calendar, AtSign, CheckCircle, X, Save } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { ApiError } from '../../../../lib/apiClient';
import { profileService } from '../../../../services';

const USERNAME_PATTERN = /^[a-z](?:[a-z0-9._-]{1,28}[a-z0-9])?$/;
const USERNAME_REQUIREMENTS = 'Use 3-30 lowercase characters, start with a letter, and only use letters, numbers, dots, hyphens, or underscores.';

const Settings = () => {
    const { setUser } = useUser();
    const [profileImage, setProfileImage] = useState(null);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [userName, setUserName] = useState('');
    const [mobileNumber, setMobileNumber] = useState('');
    const [dateOfBirth, setDateOfBirth] = useState('');
    const [file, setFile] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const [notifyByEmail, setNotifyByEmail] = useState(true);
    const [notifyBySMS, setNotifyBySMS] = useState(false);
    const [notifyByCall, setNotifyByCall] = useState(false);

    const [shareShootingScores, setShareShootingScores] = useState(false);
    const [shareLastShoots, setShareLastShoots] = useState(false);
    const [shareClubMemberships, setShareClubMemberships] = useState(false);
    const [shareEquipmentDetails, setShareEquipmentDetails] = useState(false);
    const [publicProfileVisibility, setPublicProfileVisibility] = useState('friends');

    const [showToast, setShowToast] = useState(false);
    const toastTimer = useRef(null);
    const [errors, setErrors] = useState({});
    const [memberPass, setMemberPass] = useState(null);
    const [walletLinks, setWalletLinks] = useState(null);
    const [memberPassCopied, setMemberPassCopied] = useState(false);

    const fetchUserProfile = useCallback(async () => {
        try {
            const data = await profileService.getProfile();
            setUser(data);
            setProfileFromState(data);
        } catch {
            console.error("Failed to fetch profile");
        }
    }, [setUser]);

    const setProfileFromState = (data) => {
        setFirstName(data.firstName ?? '');
        setLastName(data.lastName ?? '');
        setEmail(data.email ?? '');
        setUserName(data.userName ?? '');
        setMobileNumber(data.mobileNumber ?? '');
        setDateOfBirth(data.dateOfBirth ? new Date(data.dateOfBirth).toISOString().split('T')[0] : '');
        setProfileImage(data.profilePictureUrl ?? null);
        setNotifyByEmail(data.notifyByEmail ?? true);
        setNotifyBySMS(data.notifyBySMS ?? false);
        setNotifyByCall(data.notifyByCall ?? false);
        setShareShootingScores(data.shareShootingScores ?? false);
        setShareLastShoots(data.shareLastShoots ?? false);
        setShareClubMemberships(data.shareClubMemberships ?? false);
        setShareEquipmentDetails(data.shareEquipmentDetails ?? false);
        setPublicProfileVisibility(data.publicProfileVisibility ?? 'friends');
    };

    useEffect(() => {
        fetchUserProfile(); // always pull latest from server to avoid stale context
    }, [fetchUserProfile]);

    useEffect(() => {
        const fetchMemberPass = async () => {
            try {
                const passData = await profileService.getMemberPass();
                setMemberPass(passData);
            } catch (error) {
                console.error('Failed to load member pass:', error);
            }
        };

        fetchMemberPass();
    }, []);

    useEffect(() => {
        const fetchWalletLinks = async () => {
            try {
                const data = await profileService.getMemberPassWallet();
                setWalletLinks(data);
            } catch (error) {
                console.error('Failed to load wallet links:', error);
                setWalletLinks(null);
            }
        };

        fetchWalletLinks();
    }, []);

    const handleImageUpload = (e) => {
        const selectedFile = e.target.files[0];
        setFile(selectedFile);
        setProfileImage(URL.createObjectURL(selectedFile));
    };

    const handleCopyMemberPass = useCallback(async () => {
        if (!memberPass?.qrValue) {
            return;
        }

        await navigator.clipboard.writeText(memberPass.qrValue);
        setMemberPassCopied(true);
        window.setTimeout(() => setMemberPassCopied(false), 2200);
    }, [memberPass]);

    const handleSave = async () => {
        setIsLoading(true);
        setErrors({});
        const normalizedUserName = userName.trim().toLowerCase();

        if (!USERNAME_PATTERN.test(normalizedUserName)) {
            setErrors({ general: USERNAME_REQUIREMENTS });
            setIsLoading(false);
            return;
        }

        try {
            const updated = {
                firstName, lastName, userName: normalizedUserName, email, mobileNumber,
                dateOfBirth: dateOfBirth ? new Date(dateOfBirth).toISOString() : null,
                notifyByEmail, notifyBySMS, notifyByCall,
                shareShootingScores, shareLastShoots, shareClubMemberships, shareEquipmentDetails,
                publicProfileVisibility
            };

            await profileService.updateProfile(updated);

            if (file) {
                const formData = new FormData();
                formData.append('file', file);
                const imageData = await profileService.uploadProfilePicture(formData);
                setProfileImage(imageData.url);
                updated.profilePictureUrl = imageData.url;
            }

            const newUser = await profileService.getProfile();
            setUser(newUser);
            setProfileFromState(newUser);

            setShowToast(true);
            clearTimeout(toastTimer.current);
            toastTimer.current = setTimeout(() => setShowToast(false), 3000);
        } catch (err) {
            console.error(err);
            if (err instanceof ApiError && err.data?.errors) {
                setErrors(err.data.errors);
                return;
            }
            setErrors({ general: err.message || "Something went wrong. Try again." });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className='settings-page dashboard-component'>
            {/* Success Toast */}
            {showToast && (
                <div className="settings-toast settings-toast-success">
                    <CheckCircle size={18} />
                    <span>Profile updated successfully!</span>
                    <button onClick={() => setShowToast(false)}><X size={16} /></button>
                </div>
            )}

            {/* Error Toast */}
            {errors.general && (
                <div className="settings-toast settings-toast-error">
                    <X size={18} />
                    <span>{errors.general}</span>
                    <button onClick={() => setErrors({})}><X size={16} /></button>
                </div>
            )}

            {/* Header */}
            <div className="settings-header">
                <div className="settings-header-content">
                    <div className="settings-header-icon">
                        <User size={28} />
                    </div>
                    <div className="settings-header-text">
                        <h1>Profile Settings</h1>
                        <p>Manage your account details and preferences</p>
                    </div>
                </div>
                <button className="settings-save-btn settings-save-btn-desktop" onClick={handleSave} disabled={isLoading}>
                    <Save size={18} />
                    <span>{isLoading ? 'Saving...' : 'Save Changes'}</span>
                </button>
            </div>

            <div className="settings-layout">
                {/* Sidebar - Profile Card */}
                <div className="settings-sidebar">
                    <div className="settings-profile-card">
                        <div className="settings-avatar-wrap">
                            <img src={profileImage || avatar} alt="Profile" className="settings-avatar" />
                            <label className="settings-avatar-edit">
                                <Camera size={16} />
                                <input type="file" onChange={handleImageUpload} accept="image/*" />
                            </label>
                        </div>
                        <h3 className="settings-profile-name">{firstName} {lastName}</h3>
                        <span className="settings-profile-username">@{userName}</span>
                    </div>

                    {memberPass && (
                        <div className="settings-member-pass-card">
                            <h4>Connect Pass QR</h4>
                            <p>Use this same QR for Add Friend and Connect Perk claims at venues.</p>
                            <div className="settings-member-pass-qr">
                                <QRCodeSVG value={memberPass.qrValue} size={148} includeMargin={true} />
                            </div>
                            <button
                                className="settings-member-pass-copy"
                                onClick={handleCopyMemberPass}
                            >
                                {memberPassCopied ? 'Copied ✓' : 'Copy pass code'}
                            </button>
                            {walletLinks && (
                                <div className="settings-wallet-actions">
                                    <a
                                        className={`settings-wallet-btn apple ${walletLinks.appleWallet?.isAvailable ? '' : 'disabled'}`}
                                        href={walletLinks.appleWallet?.isAvailable ? walletLinks.appleWallet.url : undefined}
                                        onClick={(event) => {
                                            if (!walletLinks.appleWallet?.isAvailable) {
                                                event.preventDefault();
                                            }
                                        }}
                                    >
                                        Add to Apple Wallet
                                    </a>
                                    <a
                                        className={`settings-wallet-btn google ${walletLinks.googleWallet?.isAvailable ? '' : 'disabled'}`}
                                        href={walletLinks.googleWallet?.isAvailable ? walletLinks.googleWallet.url : undefined}
                                        onClick={(event) => {
                                            if (!walletLinks.googleWallet?.isAvailable) {
                                                event.preventDefault();
                                            }
                                        }}
                                    >
                                        Add to Google Wallet
                                    </a>
                                    {!walletLinks.appleWallet?.isAvailable && walletLinks.appleWallet?.message && (
                                        <p className="settings-wallet-hint">{walletLinks.appleWallet.message}</p>
                                    )}
                                    {!walletLinks.googleWallet?.isAvailable && walletLinks.googleWallet?.message && (
                                        <p className="settings-wallet-hint">{walletLinks.googleWallet.message}</p>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Main Content */}
                <div className="settings-main">
                    {/* Personal Information */}
                    <div className="settings-section">
                        <div className="settings-section-header">
                            <User size={20} />
                            <h2>Personal Information</h2>
                        </div>
                        <div className="settings-form-grid">
                            <div className="settings-form-group">
                                <label className="settings-label">First Name</label>
                                <input 
                                    type="text" 
                                    className="settings-input" 
                                    value={firstName} 
                                    onChange={(e) => setFirstName(e.target.value)} 
                                    placeholder="Enter first name"
                                />
                            </div>
                            <div className="settings-form-group">
                                <label className="settings-label">Last Name</label>
                                <input 
                                    type="text" 
                                    className="settings-input" 
                                    value={lastName} 
                                    onChange={(e) => setLastName(e.target.value)} 
                                    placeholder="Enter last name"
                                />
                            </div>
                            <div className="settings-form-group">
                                <label className="settings-label">
                                    <AtSign size={14} />
                                    Username
                                </label>
                                <input 
                                    type="text" 
                                    className="settings-input" 
                                    value={userName} 
                                    onChange={(e) => setUserName(e.target.value)} 
                                    placeholder="Enter username"
                                />
                                <small className="settings-helper-text">{USERNAME_REQUIREMENTS}</small>
                            </div>
                            <div className="settings-form-group">
                                <label className="settings-label">
                                    <Mail size={14} />
                                    Email Address
                                </label>
                                <input 
                                    type="email" 
                                    className="settings-input" 
                                    value={email} 
                                    onChange={(e) => setEmail(e.target.value)} 
                                    placeholder="Enter email"
                                />
                            </div>
                            <div className="settings-form-group">
                                <label className="settings-label">
                                    <Phone size={14} />
                                    Mobile Number
                                </label>
                                <input 
                                    type="tel" 
                                    className="settings-input" 
                                    value={mobileNumber} 
                                    onChange={(e) => setMobileNumber(e.target.value)} 
                                    placeholder="Enter mobile number"
                                />
                            </div>
                            <div className="settings-form-group">
                                <label className="settings-label">
                                    <Calendar size={14} />
                                    Date of Birth
                                </label>
                                <input 
                                    type="date" 
                                    className="settings-input" 
                                    value={dateOfBirth} 
                                    onChange={(e) => setDateOfBirth(e.target.value)} 
                                />
                            </div>
                        </div>
                    </div>

                    {/* Notification Preferences */}
                    <div className="settings-section">
                        <div className="settings-section-header">
                            <Bell size={20} />
                            <h2>Notification Preferences</h2>
                        </div>
                        <p className="settings-section-desc">Choose how you want to receive notifications</p>
                        <div className="settings-toggle-group">
                            <label className="settings-toggle">
                                <input 
                                    type="checkbox" 
                                    checked={notifyByEmail} 
                                    onChange={(e) => setNotifyByEmail(e.target.checked)} 
                                />
                                <span className="settings-toggle-slider"></span>
                                <span className="settings-toggle-label">
                                    <Mail size={16} />
                                    Email Notifications
                                </span>
                            </label>
                            <label className="settings-toggle">
                                <input 
                                    type="checkbox" 
                                    checked={notifyBySMS} 
                                    onChange={(e) => setNotifyBySMS(e.target.checked)} 
                                />
                                <span className="settings-toggle-slider"></span>
                                <span className="settings-toggle-label">
                                    <Phone size={16} />
                                    SMS Notifications
                                </span>
                            </label>
                            <label className="settings-toggle">
                                <input 
                                    type="checkbox" 
                                    checked={notifyByCall} 
                                    onChange={(e) => setNotifyByCall(e.target.checked)} 
                                />
                                <span className="settings-toggle-slider"></span>
                                <span className="settings-toggle-label">
                                    <Phone size={16} />
                                    Phone Call Notifications
                                </span>
                            </label>
                        </div>
                    </div>

                    {/* Privacy & Sharing */}
                    <div className="settings-section">
                        <div className="settings-section-header">
                            <Shield size={20} />
                            <h2>Privacy & Sharing</h2>
                        </div>
                        <p className="settings-section-desc">Control what information is visible to others</p>
                        
                        <div className="settings-visibility-select">
                            <label className="settings-label">
                                <Eye size={14} />
                                Profile Visibility
                            </label>
                            <div className="settings-radio-group">
                                <label className={`settings-radio-option ${publicProfileVisibility === 'private' ? 'active' : ''}`}>
                                    <input 
                                        type="radio" 
                                        name="visibility" 
                                        value="private" 
                                        checked={publicProfileVisibility === 'private'}
                                        onChange={(e) => setPublicProfileVisibility(e.target.value)}
                                    />
                                    <span className="settings-radio-content">
                                        <strong>Private</strong>
                                        <small>Only you can see your profile</small>
                                    </span>
                                </label>
                                <label className={`settings-radio-option ${publicProfileVisibility === 'friends' ? 'active' : ''}`}>
                                    <input 
                                        type="radio" 
                                        name="visibility" 
                                        value="friends" 
                                        checked={publicProfileVisibility === 'friends'}
                                        onChange={(e) => setPublicProfileVisibility(e.target.value)}
                                    />
                                    <span className="settings-radio-content">
                                        <strong>Friends Only</strong>
                                        <small>Only your friends can see</small>
                                    </span>
                                </label>
                                <label className={`settings-radio-option ${publicProfileVisibility === 'public' ? 'active' : ''}`}>
                                    <input 
                                        type="radio" 
                                        name="visibility" 
                                        value="public" 
                                        checked={publicProfileVisibility === 'public'}
                                        onChange={(e) => setPublicProfileVisibility(e.target.value)}
                                    />
                                    <span className="settings-radio-content">
                                        <strong>Public</strong>
                                        <small>Anyone can see your profile</small>
                                    </span>
                                </label>
                            </div>
                        </div>

                        <div className="settings-toggle-group">
                            <label className="settings-toggle">
                                <input 
                                    type="checkbox" 
                                    checked={shareShootingScores} 
                                    onChange={(e) => setShareShootingScores(e.target.checked)} 
                                />
                                <span className="settings-toggle-slider"></span>
                                <span className="settings-toggle-label">Share Shooting Scores</span>
                            </label>
                            <label className="settings-toggle">
                                <input 
                                    type="checkbox" 
                                    checked={shareLastShoots} 
                                    onChange={(e) => setShareLastShoots(e.target.checked)} 
                                />
                                <span className="settings-toggle-slider"></span>
                                <span className="settings-toggle-label">Share Recent Activity</span>
                            </label>
                            <label className="settings-toggle">
                                <input 
                                    type="checkbox" 
                                    checked={shareClubMemberships} 
                                    onChange={(e) => setShareClubMemberships(e.target.checked)} 
                                />
                                <span className="settings-toggle-slider"></span>
                                <span className="settings-toggle-label">Share Club Memberships</span>
                            </label>
                            <label className="settings-toggle">
                                <input 
                                    type="checkbox" 
                                    checked={shareEquipmentDetails} 
                                    onChange={(e) => setShareEquipmentDetails(e.target.checked)} 
                                />
                                <span className="settings-toggle-slider"></span>
                                <span className="settings-toggle-label">Share Equipment Details</span>
                            </label>
                        </div>
                    </div>

                    <button className="settings-save-btn settings-save-btn-mobile" onClick={handleSave} disabled={isLoading}>
                        <Save size={18} />
                        <span>{isLoading ? 'Saving...' : 'Save Changes'}</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Settings;
